import { DatePicker } from 'antd'
import { useId, type ReactNode } from 'react'

import { cx } from '@/Ship/Helpers/cssClass'

import { matchPreset, PERIOD_PRESETS, type PeriodRange } from '../Helpers/reportPeriod'

import styles from './Report.module.css'

const { RangePicker } = DatePicker

interface FieldProps {
    label: string
    htmlFor?: string
    /** Ширина поля: звичайне (як поле фільтра списку) або широке (період) */
    wide?: boolean
    children: ReactNode
}

/**
 * Поле фільтра з підписом. З htmlFor — <label> до конкретного контролу; без нього (складені
 * контроли: період, сегменти) — група, підписана через aria-labelledby.
 */
export const Field = ({ label, htmlFor, wide = false, children }: FieldProps) => {
    const labelId = useId()
    const className = `${cx(styles, 'field')} ${wide ? cx(styles, 'fieldWide') : ''}`
    if (htmlFor) {
        return (
            <div className={className}>
                <label className={cx(styles, 'fieldLabel')} htmlFor={htmlFor}>
                    {label}
                </label>
                {children}
            </div>
        )
    }
    return (
        <div className={className} role="group" aria-labelledby={labelId}>
            <span id={labelId} className={cx(styles, 'fieldLabel')}>
                {label}
            </span>
            {children}
        </div>
    )
}

interface ReportPeriodFieldProps {
    label: string
    value: PeriodRange | null
    onChange: (value: PeriodRange | null) => void
    /** Можна очистити (тоді «за весь час»); для звітів з обов'язковим періодом — ні */
    allowClear?: boolean
}

/** Період звіту: діапазон дат і швидкі періоди (сьогодні, тиждень, місяць…) */
export const ReportPeriodField = ({ label, value, onChange, allowClear = false }: ReportPeriodFieldProps) => {
    const activePreset = matchPreset(value)

    return (
        <Field label={label} wide>
            <div className={cx(styles, 'period')}>
                <RangePicker
                    value={value}
                    onChange={(range) => {
                        if (range?.[0] && range[1]) onChange(range as PeriodRange)
                        else if (allowClear) onChange(null)
                    }}
                    format="DD.MM.YYYY"
                    placeholder={['Дата від', 'Дата до']}
                    allowClear={allowClear}
                />
                <div className={cx(styles, 'presets')} role="group" aria-label="Швидкі періоди">
                    {PERIOD_PRESETS.map((preset) => (
                        <button
                            key={preset.key}
                            type="button"
                            className={cx(styles, activePreset === preset.key ? 'presetActive' : 'preset')}
                            aria-pressed={activePreset === preset.key}
                            onClick={() => onChange(preset.range())}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>
            </div>
        </Field>
    )
}
